import React, { Fragment } from "react";
import { Unity, useUnityContext } from "react-unity-webgl";
import { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { getStorage, ref } from "firebase/storage";
import CopyToClipboard from 'react-copy-to-clipboard';


const JumperGame = ({userData, gameData, clearData}) => {
  const navigate = useNavigate();
  const storage = getStorage();

  const [playerTextures, setPlayerTextures] = useState(null);
  const [texturesLoaded, setTexturesLoaded] = useState(false);
  const [playerAudio, setPlayerAudio] = useState(null);
  const [audioLoaded, setAudioLoaded] = useState(false);
  const [unloading, setUnloading] = useState(false);
  const [showCopy, setShowCopy] = useState(false);

  const { unityProvider, sendMessage, addEventListener, removeEventListener, isLoaded, loadingProgression, unload } =
    useUnityContext({    
      loaderUrl: "/assets/games/jumper/JumperV1.0.1.loader.js",
      dataUrl: "/assets/games/jumper/JumperV1.0.1.data",
      frameworkUrl: "/assets/games/jumper/JumperV1.0.1.framework.js",
      codeUrl: "/assets/games/jumper/JumperV1.0.1.wasm",
  });

  useEffect(()=>{
    if(userData == null || gameData == null || userData == undefined || gameData == undefined)
      window.location.href = 'https://www.littlelot.studio/gameroom';
  },[userData, gameData])

  useEffect(() => {
    async function sendTexturesToUnity(textureUrls) {
      try {
        const base64Textures = await Promise.all(
          textureUrls.map(async (textureUrl) => {
            const base64Texture = await convertTextureToBase64(textureUrl);
            return base64Texture;
          })
        );
  
        const baseTexturesObj = {};
        textureUrls.forEach((textureUrl, index) => {
          const key = Object.keys(gameData.gameDesign).find(
            (k) => gameData.gameDesign[k] === textureUrl
          );
          baseTexturesObj[key] = base64Textures[index];
        });
  
        setPlayerTextures(baseTexturesObj);
        setTexturesLoaded(true); 
      } catch (error) {
        console.error("Error loading textures:", error);
      }
    }
  
    async function convertTextureToBase64(textureUrl) {
      const response = await fetch(textureUrl);
      const blob = await response.blob();
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          const base64data = reader.result.split(',')[1];
          resolve(base64data);
        };
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    }

    if(gameData !== undefined){
      const gameDesign = gameData.gameDesign;
      const filteredDesigns = Object.keys(gameDesign)
        .filter((key) => gameDesign[key].includes("https://"))
        .reduce((obj, key) => {
          obj[key] = gameDesign[key];
          return obj;
        }, {});
    
      sendTexturesToUnity(Object.values(filteredDesigns));
    }
  }, [gameData]);

  useEffect(() => {  
    if (gameData !== undefined) {
      const gameDesign = gameData.gameDesign;
      const filteredDesigns = Object.fromEntries(
        Object.entries(gameDesign)
          .filter(([key, _]) => key.substring(key.length - 3) === "sfx")
      );
    
      setPlayerAudio(filteredDesigns);
      setAudioLoaded(true);
    }
  }, [gameData]);
  
  
  useEffect(() => {
    if (isLoaded && texturesLoaded && audioLoaded) {
      for (let key in userData) {
        sendMessage("JumperGameController", "SetUserData", key + "," + userData[key]);
      }

      for (let key in gameData) {
        if(typeof gameData[key] === "string"){
          sendMessage("JumperGameController", "SetGameData", key + "," + gameData[key]);
        }
      }

      const gameDesign = gameData.gameDesign;
      for (let key in gameDesign) {
        if(typeof gameDesign[key] === "string"){
          if(key.includes("sfx") || gameDesign[key].includes("pwr")){
            sendMessage("JumperGameController", "SetGameDesign", key + "," + gameDesign[key]);
          }
        }
      }

      for (let key in playerTextures) {
        if(typeof playerTextures[key] === "string"){
          sendMessage("JumperGameController", "SetGameTexture", key + "," + playerTextures[key]);
        }
      }

      for (let key in playerAudio) {
        if(typeof playerAudio[key] === "string"){
          sendMessage("JumperGameController", "SetGameAudio", key + "," + playerAudio[key]);
        }
      }

      sendMessage("JumperGameController", "SetGameReady", "true");
    }
  }, [isLoaded, texturesLoaded, audioLoaded, sendMessage]);

  const handleGameOver = useCallback(() => {
    console.log("GameOver");
  }, []);

  useEffect(() => {
    addEventListener("GameOverClick", handleGameOver);
    return () => {
      removeEventListener("GameOverClick", handleGameOver);
    };
  }, [addEventListener, removeEventListener, handleGameOver]);


  useEffect(() => {
    async function unloadUnity() {
      await unload();
      clearData();
      setUnloading(false);
      navigate("/gameroom");
    }

    if (unloading) {
      unloadUnity();
    }
  }, [unloading, unload, clearData, navigate]);



  const handleBackClick = () =>{
    setUnloading(true);
  }

  const handleShareClick = () =>{
    if(showCopy)
      return;

    setShowCopy(true);
    setTimeout(()=>{
      setShowCopy(false);
    }, 3000);
  }

  const containerRef = useRef(null);

  const containerStyle = {
    position: 'relative',
    width: '100%',
    height: "100%",
    overflow: 'hidden',
    marginTop: '50px',
    maxWidth: '1080px',
    margin: '0 auto',
    zIndex:"50"
  };

  const unityStyle = {
    position: 'absolute',
    width: 'auto',  // Auto width
    height: '80vh', // 80% of viewport height
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    aspectRatio: '9 / 16',  // Aspect ratio
  };

  const bodyStyle = {
    margin: '0',
    padding: '0',
    height: '100vh',  // full viewport height
    overflow: 'hidden',  // prevent scrolling
    position: "relative",
    fontFamily: "Quicksand, sans-serif",
    fontWeight: "800"
  };

  return (
    <Fragment>
      <div style={bodyStyle}>
        <img 
            src="/assets/games/gameroom/cardboard.png"
            style={{position:"absolute", top:"0", height: "100%", width:"100%", zIndex:"-10"}}
        />

        <img 
            src="/assets/games/gameroom/littlelot_logo.png"
            style={{position:"absolute", width:"50px", bottom:"20px", left:"20px"}}
        />

        <img 
            src="/assets/games/gameroom/gamecreator_logo.png"
            style={{position:"absolute", width:"50px", bottom:"20px", right:"20px"}}
        />

        <img 
            src="/assets/games/gameroom/gr_star.png"
            style={{position:"absolute", width:"20px", top:"1%", left:"15%", transform:"rotate(12deg)"}}
        />

        <img 
            src="/assets/games/gameroom/gr_star.png"
            style={{position:"absolute", width:"20px", top:"5%", left:"80%", transform:"rotate(30deg)"}}
        />

        <img 
            src="/assets/games/gameroom/gr_star.png"
            style={{position:"absolute", width:"20px", top:"30%", left:"6%", transform:"rotate(-5deg)"}}
        />

        <img 
            src="/assets/games/gameroom/gr_star.png"
            style={{position:"absolute", width:"20px", top:"60%", left:"96%", transform:"rotate(-5deg)"}}
        />
                <img 
            src="/assets/games/gameroom/gr_star.png"
            style={{position:"absolute", width:"20px", top:"50%", left:"27%", transform:"rotate(-5deg)"}}
        />

        <div 
          className="center-child" 
          style={{position:"absolute", width:"30px", height:"30px", top:"2%", left:"10px", backgroundColor:"black", borderRadius:"50%", margin:"0px 5px", cursor:"pointer", zIndex:"60"}}
          onClick={()=>handleBackClick()}
        >
            <img src="/assets/icon/back_icon_white.png" style={{width:"60%"}}/>
        </div>

        {userData !== undefined &&
          <CopyToClipboard text={"https://www.littlelot.studio/gameroom?gameId=" + userData.userId + "_" + gameData.gameId} onCopy={handleShareClick}>
            <div 
              className="center-child" 
              style={{position:"absolute", width:"30px", height:"30px", top:"2%", right:"10px", backgroundColor:"black", borderRadius:"50%", margin:"0px 5px", cursor:"pointer", zIndex:"60"}}
              onClick={()=>handleShareClick()}
            >
                <img src="/assets/icon/share_icon_white.png" style={{width:"60%"}}/>
            </div>
          </CopyToClipboard>
        }

        {gameData !== undefined &&
            <div style={{position:"absolute", display:"flex", width:"100%", height:"35px", top:"3%", justifyContent:"center", alignItems:"center"}}>
              <div style={{textAlign:"center", overflowWrap:"break-word", lineHeight:"14px", width:"70%", fontSize:"30px"}}>{gameData.gameName}</div>
            </div>
        }

        {showCopy &&
          <div style={{zIndex:"200", width:"200px", height:"50px", paddingTop:"5px", backgroundColor:"white", opacity:"0.8", position:"absolute", top:"50%", left:"50%", textAlign:"center", transform:"translate(-50%,-50%)"}}>{"Link Copied \n Let's Share Your Game!!"}</div>
        }

        {!isLoaded && (
          <p style={{color:"black", position:"absolute", top:"50%", left:"50%"}}>Loading... {Math.round(loadingProgression * 100)}%</p>
        )}
        <div ref={containerRef} style={containerStyle}>
          <Unity 
            unityProvider={unityProvider} 
            style={unityStyle} 
          />
        </div>
      </div>
    </Fragment>
  );
}

export default JumperGame;